import HoverTooltip from '@deltek/specification-client/core/Feedbacks/HoverTooltip'
import {
    ContentLibrary,
    ListingLevel,
    ManufacturerProductCatalogDetail,
    ManufacturerProductImageDetail,
    ManufacturerProductSpecDocDetail,
    ManufacturerProductVideoDetail,
} from '@deltek/specification-client/core/Models/product'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { suffixDimension } from '@deltek/specification-client/core/Utilities/stringUtils'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { ProductListingCardFooter } from '@deltek/specification-client/core/Widgets/ProductListing/Card/ProductListingCardFooter'
import { ProductListingCardLogo } from '@deltek/specification-client/core/Widgets/ProductListing/Card/ProductListingCardLogo'
import { getDocDetail } from '@deltek/specification-client/core/Widgets/ProductListing/Details/productDetailUtils'
import Card from '@material-ui/core/Card'
import CardContent from '@material-ui/core/CardContent'
import CardMedia from '@material-ui/core/CardMedia'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import { Theme, makeStyles } from '@material-ui/core/styles'
import Skeleton from '@material-ui/lab/Skeleton'
import { ListingStatus } from 'bpm/pages/ProductsPage/ProductDetailsPage/components/ListingHeaderVariants/ListingStatusVariant'
import { removeFamilyNumberFromFamilyNameRegex } from 'bpm/pages/ProductsPage/components/common'
import clsx from 'clsx'
import { processEnv } from 'components/configuration/environmentVariables'
import { addMinutes, format } from 'date-fns'
import React, { useEffect, useMemo } from 'react'
import { Link, useHistory, useLocation } from 'react-router-dom'
import Truncate from 'react-truncate'
import ProductListingCardCarousel from './ProductListingCardCarousel'

const useStyles = makeStyles((theme: Theme) => ({
    cardContainer: {
        width: 'calc(100% - 4px)', // 4px is the column separator
        height: 'calc(100% - 4px)', // 4px is the row separator
        border: 'solid 1px #AAAAAA',
        '&:hover': {
            border: '1px solid #10B091',
            boxShadow: '0px 0px 10px #B9EAE0',
        },
    },

    customCardHeader: {
        height: 60,
        maxHeight: 60,
        display: 'flex',
        justifyContent: 'space-between',
        flexWrap: 'nowrap',
    },
    mediumWeight: {
        fontWeight: 500,
    },
    cardContent: {
        height: 'calc(100% - 220px)',
    },
    productLink: {
        textDecoration: 'none',
        height: '100%',
    },
    guttered: {
        padding: theme.spacing(1.2),
    },
    grayText: {
        color: '#5E5F61',
    },
    alignText: {
        marginTop: '5px',
        [theme.breakpoints.between('sm', 'md')]: {
            width: 50,
        },
        [theme.breakpoints.up('lg')]: {
            width: 100,
        },

        [theme.breakpoints.up('xl')]: {
            width: 'auto',
        },
    },
    publishStatusDateText: {
        fontSize: pxToRem(10),
        lineHeight: '15px',
    },

    fullHeight: {
        height: '100%',
    },
    smallerLineHeight: {
        lineHeight: '20px',
    },
    customCaptionSized: {
        fontSize: pxToRem(12),
    },
    fullWidth: {
        width: '100%',
    },
    noImage: {
        textDecoration: 'none',
        textAlign: 'center',
        height: 130,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    darkGrayBackground: {
        backgroundColor: '#B1B0B1',
    },
    lightGrayBackground: {
        backgroundColor: '#c9c9c9',
    },
    largeFont: {
        fontSize: pxToRem(30),
        color: theme.palette.secondary.light,
        fontWeight: 500,
    },
    textStyle: {
        textTransform: 'uppercase',
    },
    smallFont: {
        fontSize: pxToRem(20),
        color: theme.palette.secondary.light,
        fontWeight: 500,
    },
    premiumNoImageTextContainer: {
        maxWidth: 308,
        fontWeight: 400,
        fontSize: pxToRem(36),
        lineHeight: pxToRem(42),
    },
    firmLogoImage: {
        maxHeight: 40,
        objectFit: 'contain',
        display: 'block',
        maxWidth: 190,
    },
    companyLink: {
        display: 'block',
        textDecoration: 'none',
        color: '#000',
        maxWidth: pxToRem(180),
    },
    cardContentItem: {
        paddingTop: theme.spacing(0.35),
        paddingBottom: theme.spacing(0.35),
    },
    proprietaryLabel: {
        height: 22,
        width: 81,
        fontSize: pxToRem(11),
        fontWeight: 500,
        marginTop: theme.spacing(1.5),
        position: 'absolute',
        backgroundColor: '#FD3431',
        color: theme.palette.secondary.light,
        padding: `${theme.spacing(0.37)}px ${theme.spacing(1.37)}px`,
        borderTopRightRadius: 2,
        borderBottomRightRadius: 2,
        zIndex: 1,
    },
}))

export interface ProductCardProps {
    sectionName?: string
    firmName?: string
    displayFirmName?: string
    firmLogoUrl?: string
    productName?: string
    hideProductName?: boolean
    productType?: string
    productTypeId?: number
    listingLevel?: ListingLevel
    submissionStatus?: string
    publishDate?: string
    isActive?: boolean
    isDeltekWriterDraft?: boolean
    familyName?: string
    listingId?: string
    categories?: string[]
    firmId?: string
    images?: Partial<ManufacturerProductImageDetail>[]
    videos?: Partial<ManufacturerProductVideoDetail>[]
    catalogs?: Partial<ManufacturerProductCatalogDetail>[]
    manufacturerDocs?: Partial<ManufacturerProductSpecDocDetail>[]
    partnershipLinksBIM?: string
    partnershipLinksSwatchbox?: string
    loading?: boolean
    canAutoGenerateProductSpecification?: boolean
    isDeltekUser?: boolean
    hasManufacturerOptions?: boolean
    contentLibrary?: ContentLibrary
}

export interface ListingCardMedia {
    imgUrl: string
    isVideo: boolean
    order: number
}

const ProductListingCard: React.FC<ProductCardProps> = ({
    productName = '',
    listingId,
    firmName = '',
    displayFirmName = '',
    productType = '',
    productTypeId,
    hideProductName: hideName = productName === 'Unknown Product Name',
    listingLevel = ListingLevel.Manufacturer,
    publishDate,
    familyName,
    isActive = false,
    isDeltekWriterDraft = false,
    firmLogoUrl,
    categories = [],
    firmId = '',
    images,
    videos,
    catalogs,
    manufacturerDocs,
    partnershipLinksBIM = '',
    partnershipLinksSwatchbox = '',
    loading,
    submissionStatus,
    canAutoGenerateProductSpecification,
    isDeltekUser,
    hasManufacturerOptions,
    contentLibrary,
}) => {
    const classes = useStyles()
    const history = useHistory()
    const location = useLocation()

    const basePath = useMemo(() => {
        return location.pathname.split('/')?.[1]
    }, [location])
    const isProductCardManagement = basePath === 'product-card-management'
    const listingPath = useMemo(() => {
        const showBackButtonQueryParam = isProductCardManagement
            ? '&showBackButton=false'
            : ''
        return `/${basePath}/${
            listingId
                ? 'products/product/listing/' +
                  listingId +
                  `${location.search}${showBackButtonQueryParam}`
                : 'products/all'
        }`
    }, [basePath, listingId, location, isProductCardManagement])

    const hideProductName =
        hideName ||
        (listingLevel === ListingLevel.Manufacturer &&
            submissionStatus !== ListingStatus.Completed)
    const formattedPublishDate =
        (publishDate &&
            format(
                addMinutes(
                    new Date(publishDate),
                    new Date(publishDate).getTimezoneOffset()
                ),
                'MM/dd/yyyy'
            )) ||
        ''
    const showSpecificationDoc =
        (listingLevel === ListingLevel.Premium ||
            listingLevel === ListingLevel.PremiumMasterSpec ||
            listingLevel === ListingLevel.ProductListing) &&
        (submissionStatus === ListingStatus.Completed ||
            submissionStatus === ListingStatus.PremiumMasterSpecReview ||
            submissionStatus ===
                ListingStatus.PremiumMasterSpecReviewBPMSubmitted ||
            submissionStatus === ListingStatus.BPMSubmitted) &&
        !getDocDetail(manufacturerDocs, 'ShortForm') &&
        !getDocDetail(manufacturerDocs, 'FullLength')
    const consolidatedMedia: ListingCardMedia[] = React.useMemo(() => {
        const imageContents = images
            ? images
                  .filter((each) => each.filename)
                  .map(
                      (imageItem: Partial<ManufacturerProductImageDetail>) => ({
                          imgUrl: suffixDimension({
                              filename: imageItem.filename!,
                              dimension: {
                                  width: 400,
                                  height: 300,
                              },
                          }),
                          isVideo: false,
                          order: imageItem.order ?? 0,
                      })
                  )
            : []

        const videoStills = videos
            ? videos
                  .filter((each) => each.videoStillImageUrl)
                  .map(
                      (videoItem: Partial<ManufacturerProductVideoDetail>) => ({
                          imgUrl: videoItem.videoStillImageUrl!,
                          isVideo: true,
                          order: videoItem.order ?? 0,
                      })
                  )
            : []

        return [...imageContents, ...videoStills].sort(
            (a, b) => a.order - b.order
        )
    }, [images, videos])
    useEffect(() => {
        const currentState = history.location.state as any
        const fromParams = currentState?.from?.split('?')?.[1]
        const searchParams = new URLSearchParams(
            fromParams ?? history.location.search
        )
        if (
            !history.location.search &&
            currentState?.from?.includes('/products/all')
        ) {
            history.replace({
                ...history.location,
                search: searchParams.toString(),
            })
        }
    }, [history])

    const productTypeTooltip = useMemo(
        () => (isDeltekUser ? `${productType} - ${productTypeId}` : ''),
        [isDeltekUser, productType, productTypeId]
    )

    const companyName = useMemo(
        () => (!!displayFirmName ? displayFirmName : firmName),
        [displayFirmName, firmName]
    )

    return (
        <Card
            variant="elevation"
            square
            elevation={2}
            className={clsx('productListingCardItem', classes.cardContainer)}
        >
            <GridContainer
                justifyContent="space-between"
                alignItems="center"
                className={clsx(classes.customCardHeader, classes.guttered)}
            >
                <GridItem>
                    <Link
                        target="_blank"
                        to={`/bpm/products/company/${firmId}${history.location.search}`}
                        className={clsx(classes.companyLink)}
                    >
                        {loading && (
                            <Skeleton
                                variant="rect"
                                animation="pulse"
                                height={40}
                                width={110}
                                className="loadingSkeleton"
                            />
                        )}
                        {!loading && (
                            <>
                                {firmLogoUrl ? (
                                    <img
                                        src={firmLogoUrl}
                                        className={classes.firmLogoImage}
                                        alt={companyName}
                                        title={companyName}
                                    />
                                ) : (
                                    <HoverTooltip text={companyName}>
                                        <Truncate
                                            lines={2}
                                            data-testid="firmName"
                                        >
                                            {companyName}
                                        </Truncate>
                                    </HoverTooltip>
                                )}
                            </>
                        )}
                    </Link>
                </GridItem>
                <GridItem>
                    <GridContainer
                        className={classes.fullHeight}
                        direction="column"
                        alignItems="flex-start"
                        justifyContent="space-between"
                    >
                        {processEnv.REACT_APP_EnableBadgelessCards !== '1' &&
                            listingLevel !== ListingLevel.Manufacturer && (
                                <ProductListingCardLogo
                                    listingLevel={listingLevel}
                                    loading={loading}
                                />
                            )}
                        <GridItem>
                            <Typography
                                component="div"
                                variant="caption"
                                className={clsx(
                                    classes.publishStatusDateText,
                                    classes.grayText,
                                    processEnv.REACT_APP_EnableBadgelessCards !==
                                        '1' &&
                                        (listingLevel ===
                                            ListingLevel.Manufacturer ||
                                            listingLevel ===
                                                ListingLevel.ProductListing) &&
                                        classes.alignText
                                )}
                            >
                                {!loading ? (
                                    <>
                                        {publishDate
                                            ? `Published ${formattedPublishDate} `
                                            : 'Published '}
                                        -{' '}
                                        {isDeltekWriterDraft
                                            ? 'DeltekWriterDraft'
                                            : isActive
                                            ? 'Active'
                                            : 'Inactive'}
                                    </>
                                ) : (
                                    <Skeleton
                                        variant="text"
                                        animation="pulse"
                                        width={100}
                                        className="loadingSkeleton"
                                    />
                                )}
                            </Typography>
                        </GridItem>
                    </GridContainer>
                </GridItem>
            </GridContainer>
            <CardMedia>
                {loading ? (
                    <Skeleton variant="rect" animation="pulse" height={130} />
                ) : (
                    <>
                        {processEnv.REACT_APP_EnableBadgelessCards !== '1' &&
                            listingLevel ===
                                ListingLevel.PremiumProprietary && (
                                <span className={classes.proprietaryLabel}>
                                    Proprietary
                                </span>
                            )}
                        {listingLevel === ListingLevel.Manufacturer ? (
                            <Link
                                to={listingPath}
                                target={
                                    isProductCardManagement ? '_blank' : '_self'
                                }
                                unselectable="on"
                                className={clsx(
                                    classes.noImage,
                                    classes.darkGrayBackground
                                )}
                            >
                                <Typography
                                    className={clsx(
                                        classes.largeFont,
                                        'productListingCardBasicListingPreviewText'
                                    )}
                                >
                                    {(contentLibrary ===
                                        ContentLibrary.MasterSpec ||
                                        !!!Number(
                                            processEnv.REACT_APP_ShowContentLibraryOptions
                                        )) && (
                                        <Typography
                                            className={clsx(classes.smallFont)}
                                        >
                                            MasterSpec Approved
                                        </Typography>
                                    )}
                                    <Typography
                                        className={clsx(
                                            classes.largeFont,
                                            classes.textStyle
                                        )}
                                    >
                                        {listingLevel}
                                    </Typography>
                                </Typography>
                            </Link>
                        ) : consolidatedMedia.length > 0 ? (
                            <ProductListingCardCarousel
                                media={consolidatedMedia}
                                redirectPath={listingPath}
                            />
                        ) : processEnv.REACT_APP_EnableBadgelessCards === '1' &&
                          (listingLevel === ListingLevel.Premium ||
                              listingLevel === ListingLevel.PremiumMasterSpec ||
                              listingLevel ===
                                  ListingLevel.PremiumProprietary) ? (
                            <div
                                className={clsx(
                                    classes.noImage,
                                    classes.lightGrayBackground
                                )}
                            ></div>
                        ) : (
                            <Link
                                to={listingPath}
                                target={
                                    isProductCardManagement ? '_blank' : '_self'
                                }
                                unselectable="on"
                                className={clsx(
                                    classes.noImage,
                                    classes.lightGrayBackground
                                )}
                            >
                                <Typography
                                    className={clsx(
                                        classes.largeFont,
                                        listingLevel.includes('Premium') &&
                                            classes.premiumNoImageTextContainer,
                                        'productListingCardNoImageText'
                                    )}
                                >
                                    {listingLevel &&
                                    (listingLevel.includes('Premium') ||
                                        listingLevel.includes(
                                            'Product Listing'
                                        ))
                                        ? listingLevel
                                        : listingLevel + ' Listing'}
                                </Typography>
                            </Link>
                        )}
                    </>
                )}
            </CardMedia>
            <CardContent
                component={Grid}
                container
                direction="column"
                spacing={1}
                className={clsx(classes.guttered, classes.cardContent)}
            >
                <Link
                    to={listingPath}
                    className={classes.productLink}
                    target={isProductCardManagement ? '_blank' : '_self'}
                >
                    {!loading ? (
                        <>
                            {categories?.length > 0 && (
                                <GridItem>
                                    <Typography
                                        className={clsx(
                                            classes.customCaptionSized,
                                            classes.grayText,
                                            classes.cardContentItem
                                        )}
                                    >
                                        <Truncate lines={2}>
                                            {categories
                                                .join(', ')
                                                .toUpperCase()}
                                        </Truncate>
                                    </Typography>
                                </GridItem>
                            )}
                            {productName && !hideProductName && (
                                <GridItem>
                                    <Typography
                                        variant="h6"
                                        className={clsx(
                                            'productListingCardProductName',
                                            classes.mediumWeight,
                                            classes.smallerLineHeight,
                                            classes.grayText,
                                            classes.cardContentItem
                                        )}
                                    >
                                        <Truncate lines={2}>
                                            {productName}
                                        </Truncate>
                                    </Typography>
                                </GridItem>
                            )}
                            {familyName && (
                                <GridItem>
                                    <Typography
                                        className={clsx(
                                            classes.customCaptionSized,
                                            classes.mediumWeight,
                                            classes.grayText,
                                            classes.cardContentItem
                                        )}
                                    >
                                        <HoverTooltip
                                            text={familyName}
                                            children={
                                                <Truncate lines={2}>
                                                    {familyName
                                                        .replace(
                                                            removeFamilyNumberFromFamilyNameRegex,
                                                            ''
                                                        )
                                                        .toUpperCase()}
                                                </Truncate>
                                            }
                                        />
                                    </Typography>
                                </GridItem>
                            )}
                            <GridItem className={classes.fullWidth}>
                                <Typography
                                    className={clsx(
                                        classes.customCaptionSized,
                                        classes.grayText,
                                        classes.cardContentItem
                                    )}
                                >
                                    <HoverTooltip
                                        text={productTypeTooltip}
                                        children={
                                            <Truncate lines={2}>
                                                {productType}
                                            </Truncate>
                                        }
                                    />
                                </Typography>
                            </GridItem>
                        </>
                    ) : (
                        <>
                            {/* categories */}
                            <Skeleton
                                variant="text"
                                animation="pulse"
                                width={100}
                                className="loadingSkeleton"
                            />

                            {/* Product Name */}
                            <Skeleton
                                variant="text"
                                animation="pulse"
                                width={70}
                                className="loadingSkeleton"
                            />

                            {/* Family Name */}
                            <Skeleton
                                variant="text"
                                animation="pulse"
                                width={150}
                                className="loadingSkeleton"
                            />

                            {/* Product Type */}
                            <Skeleton
                                variant="rect"
                                animation="pulse"
                                height={20}
                                width={200}
                                className="loadingSkeleton"
                            />
                        </>
                    )}
                </Link>
            </CardContent>
            <ProductListingCardFooter
                listingLevel={listingLevel}
                loading={loading}
                firmName={firmName}
                familyName={familyName}
                catalogs={catalogs}
                partnershipLinkBIM={partnershipLinksBIM}
                partnershipLinkSwatchbox={
                    !!Number(processEnv.REACT_APP_ShowSwatchbox)
                        ? partnershipLinksSwatchbox
                        : ''
                }
                manufacturerDocs={manufacturerDocs}
                listingId={listingId}
                showSpecificationDoc={
                    !!canAutoGenerateProductSpecification &&
                    showSpecificationDoc
                }
                showManufacturerOptionItem={
                    [
                        ListingLevel.Premium,
                        ListingLevel.PremiumMasterSpec,
                        ListingLevel.ProductListing,
                    ].includes(listingLevel) && hasManufacturerOptions
                }
                enableBadgelessCards={
                    processEnv.REACT_APP_EnableBadgelessCards === '1' &&
                    [
                        ListingLevel.Premium,
                        ListingLevel.PremiumMasterSpec,
                    ].includes(listingLevel)
                }
            />
        </Card>
    )
}

export default ProductListingCard
